import { useMutation } from '@apollo/client';
import type { OptionalMetadata, SecurityGroupsInput } from '@communities/community/domain';
import { CREATE_COMMUNITY } from '@communities/community/graphql/createCommunity';
import { GET_COMMUNITIES_IN_GROUP } from '@communities/community/graphql/getCommunitiesInGroup';
import { GENERATE_COMMUNITY_PROMPTS } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useRef, useState } from 'react';

export interface CreateCommunityInput {
  name: string;
  optionalMetadata?: OptionalMetadata;
  securityGroups?: SecurityGroupsInput;
}

interface UseCreateCommunityOptions {
  onPromptWarnings?: (warnings: DomainError[]) => void;
}

export function useCreateCommunity(options?: UseCreateCommunityOptions) {
  const optionsRef = useRef(options);

  optionsRef.current = options;
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  // Community creation mutation
  const [createCommunityMutation, { loading: isCreating }] = useMutation(CREATE_COMMUNITY);

  // Prompt generation mutation (prompts are auto-promoted to prod on creation)
  const [generatePromptsMutation, { loading: isGeneratingPrompt }] = useMutation(GENERATE_COMMUNITY_PROMPTS);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const generateInitialPrompts = useCallback(
    async (communityId: string): Promise<DomainError[] | undefined> => {
      try {
        await generatePromptsMutation({
          variables: { communityId, input: { labelFeedback: [] } },
        });

        return undefined;
      } catch (promptError) {
        return transformErrors(promptError);
      }
    },
    [generatePromptsMutation]
  );

  const createCommunity = useCallback(
    async (communityGroupId: string, input: CreateCommunityInput): Promise<string | null> => {
      try {
        track('CreateCommunityInitiated', {
          communityGroupId,
          communityName: input.name,
        });

        // Step 1: Create the community
        const result = await createCommunityMutation({
          variables: { input: { ...input, communityGroupId } },
          refetchQueries: communityGroupId ? [{ query: GET_COMMUNITIES_IN_GROUP, variables: { communityGroupId } }] : [],
          awaitRefetchQueries: true,
        });

        const communityId = result.data?.createCommunity?.id;

        if (communityId) {
          track('CreateCommunitySucceeded', { communityGroupId, communityId });

          // Step 2: Generate prompts (auto-promoted to prod by upstream API)
          const promptWarnings = await generateInitialPrompts(communityId);

          if (promptWarnings) {
            optionsRef.current?.onPromptWarnings?.(promptWarnings);
          }
        }

        return communityId;
      } catch (error) {
        track('CreateCommunityFailed', {
          communityGroupId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return null;
      }
    },
    [createCommunityMutation, generateInitialPrompts]
  );

  return {
    createCommunity,
    isCreating,
    isGenerating: isGeneratingPrompt,
    errors,
    resetErrors,
  };
}
